'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { useSubmitOrganization } from '@/app/login/hooks/useSubmitOrganization';
import { useProviderSignup } from '@/app/signup/hooks/useProviderSignup';
import { AuthLayout } from '@/components/Layout/AuthLayout';
import { LoadingOverlay } from '@/components/Load/loading-overlay';
import { AuthError } from '@/components/ui/auth-error';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RequiredBadge } from '@/components/ui/required-badge';
import { Separator } from '@/components/ui/separator';

interface SignUpFormData {
  confirmPassword: string;
  email: string;
  fullName: string;
  organizationName: string;
  password: string;
  acceptedTerms: boolean;
}

export function SignUpForm() {
  const [formData, setFormData] = useState<SignUpFormData>({
    confirmPassword: '',
    email: '',
    fullName: '',
    organizationName: '',
    password: '',
    acceptedTerms: false,
  });

  const searchParams = useSearchParams();
  const queryError = searchParams.get('error');

  const { handleProviderSignup, error: providerError } = useProviderSignup();
  const { handleSubmitOrganization, isLoading, validationErrors } = useSubmitOrganization();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmitOrganization(formData);
  };

  return (
    <AuthLayout title="組織登録" subTitle="" description="">
      {providerError && <AuthError message={providerError} variant="error" />}
      {queryError && <AuthError message={queryError} variant="error" />}
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="organizationName" className="text-blue-700">
            組織名
            <RequiredBadge />
          </Label>
          <Input
            id="organizationName"
            name="organizationName"
            type="text"
            required
            placeholder="例: 株式会社サンプル（3文字以上）"
            value={formData.organizationName}
            onChange={onChange}
            disabled={isLoading}
            className="border-blue-200 bg-blue-50/50 focus-visible:ring-blue-500 text-blue-700 placeholder:text-blue-400"
          />
          {validationErrors.OrganizationName && (
            <p className="text-sm text-red-600">{validationErrors.OrganizationName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="fullName" className="text-blue-700">
            氏名
            <RequiredBadge />
          </Label>
          <Input
            id="fullName"
            name="fullName"
            type="text"
            required
            placeholder="例: 山田 太郎"
            value={formData.fullName}
            onChange={onChange}
            disabled={isLoading}
            className="border-blue-200 bg-blue-50/50 focus-visible:ring-blue-500 text-blue-700 placeholder:text-blue-400"
          />
          {validationErrors.FullName && (
            <p className="text-sm text-red-600">{validationErrors.FullName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-blue-700">
            メールアドレス
            <RequiredBadge />
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            placeholder="tes-navi@example.com"
            value={formData.email}
            onChange={onChange}
            disabled={isLoading}
            className="border-blue-200 bg-blue-50/50 focus-visible:ring-blue-500 text-blue-700 placeholder:text-blue-400"
          />
          {validationErrors.Email && (
            <p className="text-sm text-red-600">{validationErrors.Email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-blue-700">
            パスワード
            <RequiredBadge />
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            required
            placeholder="8文字以上"
            value={formData.password}
            onChange={onChange}
            disabled={isLoading}
            className="border-blue-200 bg-blue-50/50 focus-visible:ring-blue-500 text-blue-700 placeholder:text-blue-400"
          />
          {validationErrors.Password && (
            <p className="text-sm text-red-600">{validationErrors.Password}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-blue-700">
            パスワード（再入力）
            <RequiredBadge />
          </Label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            placeholder="パスワードを再入力"
            value={formData.confirmPassword}
            onChange={onChange}
            disabled={isLoading}
            className="border-blue-200 bg-blue-50/50 focus-visible:ring-blue-500 text-blue-700 placeholder:text-blue-400"
          />
          {validationErrors.PasswordConfirm && (
            <p className="text-sm text-red-600">{validationErrors.PasswordConfirm}</p>
          )}
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="acceptedTerms"
            checked={formData.acceptedTerms}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, acceptedTerms: checked === true }))
            }
            disabled={isLoading}
            className="mt-0.5 border-blue-300"
          />
          <Label htmlFor="acceptedTerms" className="text-sm font-normal text-blue-700">
            <Link href="/terms" target="_blank" className="text-blue-700 underline">
              利用規約
            </Link>
            ・
            <Link href="/privacy" target="_blank" className="text-blue-700 underline">
              プライバシーポリシー
            </Link>
            に同意します
          </Label>
        </div>

        <Button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          disabled={isLoading || !formData.acceptedTerms}
        >
          {isLoading ? '組織を作成中...' : '利用開始'}
        </Button>
      </form>

      <div className="mt-4">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full bg-blue-200" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-blue-50 px-2 text-blue-500">または</span>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <Button
            variant="outline"
            className="w-full bg-blue-100 border-blue-200 text-blue-800 hover:bg-blue-200 hover:text-blue-900 transition-colors"
            disabled={isLoading}
            onClick={() => handleProviderSignup('google')}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                fill="#EA4335"
              />
              <path
                d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                fill="#4285F4"
              />
              <path
                d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                fill="#FBBC05"
              />
              <path
                d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                fill="#34A853"
              />
            </svg>
            Googleで新規登録
          </Button>
          <Button
            variant="outline"
            className="w-full bg-gray-900 border-gray-800 text-white hover:bg-gray-700 hover:text-white-700 transition-colors"
            disabled={isLoading}
            onClick={() => handleProviderSignup('github')}
          >
            <svg
              className="mr-2 h-4 w-4"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
            </svg>
            GitHubで新規登録
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <p className="text-center text-sm text-blue-600">
          すでにアカウントをお持ちですか？{' '}
          <Link
            href="/login"
            className="font-medium text-blue-700 hover:text-blue-800 hover:underline"
          >
            ログイン
          </Link>
        </p>
      </div>
      <LoadingOverlay isLoading={isLoading} message="組織を作成しています..." />
    </AuthLayout>
  );
}
